import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { ChallengeStorageService } from './challenge-storage.service';

export interface ChallengeTime {
    timeRemaining: number | null;
    timeElapsed: number | null;
}

@Injectable({
    providedIn: 'root',
})
export class ChallengeTimerService {
    readonly #challengeStorageService = inject(ChallengeStorageService);

    private time$ = new BehaviorSubject<ChallengeTime | null>(null);
    private timerSubscription: Subscription | null = null;

    /**
     * Starts the challenge token countdown timer
     * @returns Observable that emits the remaining time in seconds
     */
    startTimer(): Observable<ChallengeTime | null> {
        this.stopTimer();

        const token = this.#challengeStorageService.getAuthChallengeToken();
        if (!token) {
            this.time$.next(null);
            return this.time$.asObservable();
        }

        try {
            const payload = this.parseJwt(token);
            if (!payload || !payload.exp || !payload.iat) {
                this.time$.next(null);
                return this.time$.asObservable();
            }

            const updateTimer = () => {
                const currentTime = Math.floor(Date.now() / 1000);
                const timeLeft = payload.exp - currentTime;
                const timeElapsed = currentTime - payload.iat;
                this.time$.next({
                    timeRemaining: timeLeft <= 0 ? 0 : timeLeft,
                    timeElapsed,
                });
            };

            updateTimer();
            this.timerSubscription = timer(0, 1000).subscribe(() => {
                updateTimer();
            });
        } catch (e) {
            this.time$.next(null);
        }

        return this.time$.asObservable();
    }

    /**
     * Stops the countdown timer
     */
    stopTimer(): void {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }
    }

    /**
     * Resets the timer when a challenge is verified
     */
    resetTimer(): void {
        this.startTimer();
    }

    /**
     * Formats the time remaining into MM:SS
     * @param seconds Time in seconds
     * @returns Formatted time string
     */
    formatTimeRemaining(seconds: number | null): string {
        if (seconds === null) return '--:--';

        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    /**
     * Handle token expiration
     */
    private handleExpiration(): void {
        this.stopTimer();
        this.#challengeStorageService.clearChallenge();
        window.location.reload();
    }

    /**
     * Parse JWT token
     */
    private parseJwt(token: string): any {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join(''),
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            return null;
        }
    }
}
